import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  DatePicker,
  Descriptions,
  Drawer,
  Dropdown,
  Empty,
  Input,
  Radio,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  DeleteOutlined,
  DownOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { supabase } from '@/lib/supabase'
import { formatCST, nowCST, pickerToUTCISO } from '@/lib/time'
import { useDebounced, useIsMobile } from '@/lib/hooks'
import { DebouncedSearchInput } from '@/components/DebouncedSearchInput'
import { AdminFilterBar } from '@/components/AdminFilterBar'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { IpAddressLookup } from '@/components/IpAddressLookup'

export const Route = createFileRoute('/_authed/danmaku')({
  component: DanmakuPage,
})

/* ── 类型 ───────────────────────────────────────────────────── */

type Status = 'visible' | 'hidden' | 'shadow' | 'pending' | 'deleted'
type SetableStatus = Extract<Status, 'visible' | 'hidden' | 'shadow'>
type ModeratorKind = 'human' | 'ai' | null
type TabKey = 'unreviewed' | 'ai' | 'human' | 'all'
type SortKey = 'created_desc' | 'v_time_asc'
type IdentityKind = 'auth' | 'legacy' | 'dev' | 'none'

interface DanmakuRow {
  id: number
  content: string
  episode_id: number
  episode_number: number | null
  anime_id: number | null
  anime_title: string | null
  v_time: number
  mode: 'scroll' | 'top' | 'bottom'
  color: string
  user_id: string | null
  legacy_user_id: number | null
  device_key: string | null
  user_username: string | null
  user_display_name: string | null
  status: Status
  moderator_kind: ModeratorKind
  moderated_at: string | null
  moderated_by: string | null
  report_count: number
  ip_address?: string | null
  created_at: string
}

interface ListResult {
  ok: boolean
  total?: number
  rows?: Array<DanmakuRow>
  error?: string
}

interface TabCounts {
  all: number
  ai: number
  human: number
  unreviewed: number
}

interface CountsResult {
  ok: boolean
  counts?: TabCounts
  error?: string
}

interface AnimeOption {
  id: number
  title: string
}

interface EpisodeOption {
  id: number
  episode_number: number | null
}

/* ── i18n & 映射 ─────────────────────────────────────────────── */

const STATUS_LABELS: Record<Status, string> = {
  visible: '可见',
  hidden: '隐藏',
  shadow: '影子',
  pending: '待审',
  deleted: '已删',
}

const SETABLE_STATUS_OPTIONS: Array<{ value: SetableStatus; label: string }> = (
  ['visible', 'hidden', 'shadow'] as const
).map((s) => ({ value: s, label: STATUS_LABELS[s] }))

const ALL_STATUS_OPTIONS = (
  Object.entries(STATUS_LABELS) as Array<[Status, string]>
).map(([value, label]) => ({ value, label }))

const SORT_LABELS: Record<SortKey, string> = {
  created_desc: '最新发布',
  v_time_asc: '按播放时间',
}

const MODE_LABELS: Record<DanmakuRow['mode'], string> = {
  scroll: '滚动',
  top: '顶部',
  bottom: '底部',
}

const IDENTITY_TAG_LABELS: Record<Exclude<IdentityKind, 'none'>, string> = {
  auth: '注册',
  legacy: '老',
  dev: '匿名',
}

const TAB_META: Array<{
  key: TabKey
  label: string
  emptyTitle: string
  emptyHint: string | React.ReactNode
}> = [
  {
    key: 'unreviewed',
    label: '未审',
    emptyTitle: '没有未审弹幕',
    emptyHint: (
      <>
        所有弹幕都已被 AI 或人工处理过。被举报的弹幕去{' '}
        <Link to="/reports">/reports</Link> 受理。
      </>
    ),
  },
  {
    key: 'ai',
    label: 'AI 已处理',
    emptyTitle: 'AI 还没处理过任何弹幕',
    emptyHint: 'AI 边缘函数可能未运行；可在「未审」tab 手动处理。',
  },
  {
    key: 'human',
    label: '人工已审',
    emptyTitle: '尚未做过人工弹幕审核',
    emptyHint: '从「未审」tab 开始巡检。',
  },
  {
    key: 'all',
    label: '全部',
    emptyTitle: '当前过滤下没有弹幕',
    emptyHint: '试着清空一些过滤条件。',
  },
]

/** AntD Badge.status / color 映射（颜色 + 形状双编码，纯 AntD 原生） */
function statusBadgeProps(s: Status): {
  status?: 'success' | 'warning' | 'error' | 'processing' | 'default'
  color?: string
} {
  switch (s) {
    case 'visible':
      return { status: 'success' }
    case 'hidden':
      return { status: 'warning' }
    case 'shadow':
      return { color: 'purple' }
    case 'pending':
      return { status: 'processing' }
    case 'deleted':
      return { status: 'default' }
    default:
      return { status: 'default' }
  }
}

function identityKindOf(row: DanmakuRow): IdentityKind {
  if (row.user_id) return 'auth'
  if (row.legacy_user_id != null) return 'legacy'
  if (row.device_key) return 'dev'
  return 'none'
}

function identityFingerprint(row: DanmakuRow): string {
  const kind = identityKindOf(row)
  if (kind === 'auth') return row.user_id!.slice(0, 8) + '…'
  if (kind === 'legacy') return `#${row.legacy_user_id}`
  if (kind === 'dev') return row.device_key!.slice(0, 8) + '…'
  return '—'
}

function identityCopyValue(row: DanmakuRow): string | undefined {
  const kind = identityKindOf(row)
  if (kind === 'auth') return row.user_id ?? undefined
  if (kind === 'legacy') return String(row.legacy_user_id)
  if (kind === 'dev') return row.device_key ?? undefined
  return undefined
}

function authorDisplay(row: DanmakuRow): string {
  return (
    row.user_display_name?.trim() ||
    row.user_username?.trim() ||
    (identityKindOf(row) === 'legacy' ? `legacy 用户` : '') ||
    (identityKindOf(row) === 'dev' ? '匿名' : '') ||
    '—'
  )
}

function authorRPCIdentity(
  row: DanmakuRow,
): { kind: 'user_id' | 'legacy_user_id' | 'device_key'; value: string } | null {
  if (row.user_id) return { kind: 'user_id', value: row.user_id }
  if (row.legacy_user_id != null)
    return { kind: 'legacy_user_id', value: String(row.legacy_user_id) }
  if (row.device_key) return { kind: 'device_key', value: row.device_key }
  return null
}

/* ── helpers ────────────────────────────────────────────────── */

const PAGE_SIZE = 50
const BULK_HARD_CAP = 500

function formatDateTime(
  t: string | null | undefined,
  fmt = 'YYYY-MM-DD HH:mm',
) {
  return formatCST(t, fmt)
}

function formatBadge(n: number): string {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}w`
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`
  return String(n)
}

/** m:ss 简写（admin 表格扫读用） */
function formatVTime(s: number): string {
  const total = Math.max(0, Math.floor(s))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const sec = total % 60
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
    : `${m}:${String(sec).padStart(2, '0')}`
}

/** m:ss.mmm 完整精度（详情抽屉用） */
function formatVTimeFull(s: number): string {
  const total = Math.max(0, s)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const sec = total - h * 3600 - m * 60
  const secStr = sec.toFixed(3).padStart(6, '0')
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${secStr}`
    : `${m}:${secStr}`
}

/* ── 主组件 ─────────────────────────────────────────────────── */

function DanmakuPage() {
  const qc = useQueryClient()
  const { message, modal } = AntdApp.useApp()
  const isMobile = useIsMobile()

  const [tab, setTab] = useState<TabKey>('unreviewed')
  const [statusFilter, setStatusFilter] = useState<Status[]>([])
  const [animeId, setAnimeId] = useState<number | null>(null)
  const [episodeId, setEpisodeId] = useState<number | null>(null)
  const [animeSearchInput, setAnimeSearchInput] = useState('')
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('')
  const [debouncedDeviceKey, setDebouncedDeviceKey] = useState('')
  const [debouncedKeyword, setDebouncedKeyword] = useState('')
  const [searchResetKey, setSearchResetKey] = useState(0)
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [sort, setSort] = useState<SortKey>('created_desc')
  const [page, setPage] = useState(1)
  const [detail, setDetail] = useState<DanmakuRow | null>(null)
  const [selectedKeys, setSelectedKeys] = useState<number[]>([])

  const debouncedAnimeSearch = useDebounced(animeSearchInput.trim(), 250)

  useEffect(() => {
    setPage(1)
    setSelectedKeys([])
  }, [
    tab,
    statusFilter,
    animeId,
    episodeId,
    debouncedUserSearch,
    debouncedDeviceKey,
    debouncedKeyword,
    range?.[0]?.valueOf(),
    range?.[1]?.valueOf(),
    sort,
  ])

  useEffect(() => {
    setEpisodeId(null)
  }, [animeId])

  // 是否处于"有筛选条件"状态 —— 空状态时用来决定按钮
  const hasAnyNarrowing =
    !!animeId ||
    !!episodeId ||
    !!debouncedUserSearch ||
    !!debouncedDeviceKey ||
    !!debouncedKeyword ||
    statusFilter.length > 0 ||
    !!range

  const clearAllFilters = () => {
    setStatusFilter([])
    setAnimeId(null)
    setEpisodeId(null)
    setDebouncedUserSearch('')
    setDebouncedDeviceKey('')
    setDebouncedKeyword('')
    setSearchResetKey((k) => k + 1)
    setRange(null)
  }

  /* ── 主列表查询 */
  const listQuery = useQuery({
    queryKey: [
      'admin_danmaku',
      {
        tab,
        statusFilter,
        animeId,
        episodeId,
        debouncedUserSearch,
        debouncedDeviceKey,
        debouncedKeyword,
        from: pickerToUTCISO(range?.[0]),
        to: pickerToUTCISO(range?.[1]),
        sort,
        page,
      },
    ],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('list_danmaku', {
          p_tab: tab,
          p_status: statusFilter.length > 0 ? statusFilter : null,
          p_anime_id: animeId,
          p_episode_id: episodeId,
          p_user_search: debouncedUserSearch || null,
          p_device_key: debouncedDeviceKey || null,
          p_keyword: debouncedKeyword || null,
          p_from: pickerToUTCISO(range?.[0]),
          p_to: pickerToUTCISO(range?.[1]),
          p_sort: sort,
          p_limit: PAGE_SIZE,
          p_offset: (page - 1) * PAGE_SIZE,
        })
      if (error) throw error
      const r = data as ListResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return { rows: r.rows ?? [], total: r.total ?? 0 }
    },
  })

  /* ── tab 计数 */
  const countsQuery = useQuery({
    queryKey: [
      'admin_danmaku_counts',
      {
        statusFilter,
        animeId,
        episodeId,
        debouncedUserSearch,
        debouncedDeviceKey,
        debouncedKeyword,
        from: pickerToUTCISO(range?.[0]),
        to: pickerToUTCISO(range?.[1]),
      },
    ],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('get_danmaku_tab_counts', {
          p_status: statusFilter.length > 0 ? statusFilter : null,
          p_anime_id: animeId,
          p_episode_id: episodeId,
          p_user_search: debouncedUserSearch || null,
          p_device_key: debouncedDeviceKey || null,
          p_keyword: debouncedKeyword || null,
          p_from: pickerToUTCISO(range?.[0]),
          p_to: pickerToUTCISO(range?.[1]),
        })
      if (error) throw error
      const r = data as CountsResult
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r.counts ?? null
    },
    staleTime: 30_000,
  })

  /* ── 番剧搜索 */
  const animeOptionsQuery = useQuery({
    queryKey: ['admin_danmaku_anime_search', debouncedAnimeSearch],
    enabled: debouncedAnimeSearch.length >= 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('animes')
        .select('id, title')
        .ilike('search_title', `%${debouncedAnimeSearch}%`)
        .order('view_count', { ascending: false, nullsFirst: false })
        .limit(20)
      if (error) throw error
      return (data ?? []) as Array<AnimeOption>
    },
    staleTime: 60_000,
  })

  const currentAnimeQuery = useQuery({
    queryKey: ['admin_danmaku_current_anime', animeId],
    enabled: !!animeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('animes')
        .select('id, title')
        .eq('id', animeId!)
        .maybeSingle()
      if (error) throw error
      return (data ?? null) as AnimeOption | null
    },
  })

  const episodeOptionsQuery = useQuery({
    queryKey: ['admin_danmaku_episodes', animeId],
    enabled: !!animeId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('episodes')
        .select('id, episode_number')
        .eq('anime_id', animeId!)
        .order('episode_number', { ascending: true })
        .limit(500)
      if (error) throw error
      return (data ?? []) as Array<EpisodeOption>
    },
    staleTime: 60_000,
  })

  /* ── mutations */
  const setStatusMutation = useMutation({
    mutationFn: async (params: {
      id: number
      status: SetableStatus
      note: string | null
    }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('set_danmaku_status', {
          p_id: params.id,
          p_status: params.status,
          p_note: params.note,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string; unchanged?: boolean }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r, vars) => {
      message.success(
        r.unchanged ? '已是该状态' : `已改为「${STATUS_LABELS[vars.status]}」`,
      )
      qc.invalidateQueries({ queryKey: ['admin_danmaku'] })
      qc.invalidateQueries({ queryKey: ['admin_danmaku_counts'] })
    },
    onError: (e: Error) => message.error(e.message || '保存失败'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (params: { id: number; note: string }) => {
      if (params.note.trim().length < 5) throw new Error('reason_too_short')
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('delete_danmaku', {
          p_id: params.id,
          p_note: params.note.trim(),
        })
      if (error) throw error
      const r = data as {
        ok: boolean
        error?: string
        cascaded_reports?: number
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r) => {
      message.success(
        `已删除${r.cascaded_reports ? `（级联清掉 ${r.cascaded_reports} 条举报）` : ''}`,
      )
      qc.invalidateQueries({ queryKey: ['admin_danmaku'] })
      qc.invalidateQueries({ queryKey: ['admin_danmaku_counts'] })
      setDetail(null)
    },
    onError: (e: Error) => {
      if (e.message === 'reason_too_short') {
        message.error('删除必须填写不少于 5 字的备注')
      } else {
        message.error(e.message || '删除失败')
      }
    },
  })

  const bulkSetStatusMutation = useMutation({
    mutationFn: async (params: {
      ids: number[]
      status: SetableStatus
      note: string | null
    }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('bulk_set_danmaku_status', {
          p_ids: params.ids,
          p_status: params.status,
          p_note: params.note,
        })
      if (error) throw error
      const r = data as {
        ok: boolean
        error?: string
        changed?: number
        requested?: number
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r, vars) => {
      message.success(
        `批量改为「${STATUS_LABELS[vars.status]}」：影响 ${r.changed ?? 0} / ${r.requested ?? 0} 条`,
      )
      qc.invalidateQueries({ queryKey: ['admin_danmaku'] })
      qc.invalidateQueries({ queryKey: ['admin_danmaku_counts'] })
      setSelectedKeys([])
    },
    onError: (e: Error) => message.error(e.message || '批量保存失败'),
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async (params: { ids: number[]; note: string }) => {
      if (params.note.trim().length < 5) throw new Error('reason_too_short')
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('bulk_delete_danmaku', {
          p_ids: params.ids,
          p_note: params.note.trim(),
        })
      if (error) throw error
      const r = data as {
        ok: boolean
        error?: string
        deleted?: number
        requested?: number
        cascaded_reports_total?: number
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r) => {
      message.success(
        `已删除 ${r.deleted ?? 0} / ${r.requested ?? 0} 条${
          r.cascaded_reports_total
            ? `（级联清掉 ${r.cascaded_reports_total} 条举报）`
            : ''
        }`,
      )
      qc.invalidateQueries({ queryKey: ['admin_danmaku'] })
      qc.invalidateQueries({ queryKey: ['admin_danmaku_counts'] })
      setSelectedKeys([])
    },
    onError: (e: Error) => {
      if (e.message === 'reason_too_short') {
        message.error('删除必须填写不少于 5 字的备注')
      } else {
        message.error(e.message || '批量删除失败')
      }
    },
  })

  const banUserMutation = useMutation({
    mutationFn: async (params: {
      identity: {
        kind: 'user_id' | 'legacy_user_id' | 'device_key'
        value: string
      }
      note: string
    }) => {
      if (params.note.trim().length < 5) throw new Error('reason_too_short')
      const payload: Record<string, unknown> = {
        p_note: params.note.trim(),
        p_user_id: null,
        p_legacy_user_id: null,
        p_device_key: null,
      }
      if (params.identity.kind === 'user_id')
        payload.p_user_id = params.identity.value
      else if (params.identity.kind === 'legacy_user_id')
        payload.p_legacy_user_id = Number(params.identity.value)
      else payload.p_device_key = params.identity.value

      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('ban_user_danmaku', payload)
      if (error) throw error
      const r = data as {
        ok: boolean
        error?: string
        shadowed?: number
        matched?: number
        identity?: string
      }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (r) => {
      message.success(
        `已 shadow ${r.shadowed ?? 0} / ${r.matched ?? 0} 条该身份弹幕`,
      )
      qc.invalidateQueries({ queryKey: ['admin_danmaku'] })
      qc.invalidateQueries({ queryKey: ['admin_danmaku_counts'] })
    },
    onError: (e: Error) => {
      if (e.message === 'reason_too_short') {
        message.error('Ban 必须填写不少于 5 字的备注')
      } else if (e.message === 'too_many_matches') {
        message.error('该身份命中 > 5000 条弹幕，超过保护上限。请联系 DBA。')
      } else {
        message.error(e.message || 'Ban 失败')
      }
    },
  })

  /* ── 行内动作 */
  const handleRowSetStatus = (row: DanmakuRow, status: SetableStatus) => {
    if (row.status === status) {
      message.info('已是该状态')
      return
    }
    setStatusMutation.mutate({ id: row.id, status, note: null })
  }

  const handleRowDelete = (row: DanmakuRow) => {
    let note = ''
    modal.confirm({
      title: `硬删弹幕 #${row.id}？`,
      content: (
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            不可恢复。触发器会级联清掉所有关联举报。审计日志会保留 200
            字内容摘要。
          </Typography.Text>
          <Input.TextArea
            autoFocus
            placeholder="备注（必填 ≥5 字）"
            onChange={(e) => {
              note = e.target.value
            }}
            autoSize={{ minRows: 2, maxRows: 3 }}
            maxLength={500}
          />
        </Space>
      ),
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        if (note.trim().length < 5) {
          message.warning('删除必须填写不少于 5 字的备注')
          return Promise.reject(new Error('reason_too_short'))
        }
        return deleteMutation.mutateAsync({ id: row.id, note })
      },
    })
  }

  /* ── 批量动作 */
  const handleBulkSetStatus = (status: SetableStatus) => {
    if (selectedKeys.length === 0) return
    if (selectedKeys.length > BULK_HARD_CAP) {
      message.error(`一次最多处理 ${BULK_HARD_CAP} 条`)
      return
    }
    let note = ''
    modal.confirm({
      title: `批量改为「${STATUS_LABELS[status]}」（${selectedKeys.length} 条）`,
      content: (
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            备注会写入每条 audit log。空则记 admin_bulk_
            {status === 'visible' ? 'restore' : status}_danmaku。
          </Typography.Text>
          <Input.TextArea
            placeholder="备注（可选）"
            onChange={(e) => {
              note = e.target.value
            }}
            autoSize={{ minRows: 2, maxRows: 3 }}
            maxLength={500}
          />
        </Space>
      ),
      okText: '执行',
      cancelText: '取消',
      onOk: () =>
        bulkSetStatusMutation.mutateAsync({
          ids: selectedKeys,
          status,
          note: note.trim() || null,
        }),
    })
  }

  const handleBulkDelete = () => {
    if (selectedKeys.length === 0) return
    if (selectedKeys.length > BULK_HARD_CAP) {
      message.error(`一次最多删除 ${BULK_HARD_CAP} 条`)
      return
    }
    let note = ''
    modal.confirm({
      title: `批量硬删（${selectedKeys.length} 条）？`,
      content: (
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <Typography.Text type="warning" style={{ fontSize: 12 }}>
            不可恢复。每条 danmaku 的关联举报会被级联清掉。每条独立写 audit
            log。
          </Typography.Text>
          <Input.TextArea
            autoFocus
            placeholder="备注（必填 ≥5 字）"
            onChange={(e) => {
              note = e.target.value
            }}
            autoSize={{ minRows: 2, maxRows: 3 }}
            maxLength={500}
          />
        </Space>
      ),
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        if (note.trim().length < 5) {
          message.warning('备注必须 ≥5 字')
          return Promise.reject(new Error('reason_too_short'))
        }
        return bulkDeleteMutation.mutateAsync({ ids: selectedKeys, note })
      },
    })
  }

  /* ── 列定义 */
  const columns: ColumnsType<DanmakuRow> = useMemo(
    () => [
      {
        title: '内容',
        render: (_: unknown, row) => (
          <div>
            <Tooltip title={row.content} mouseEnterDelay={0.4}>
              <Typography.Paragraph
                ellipsis={{ rows: 2 }}
                style={{ marginBottom: 4 }}
              >
                {row.content}
              </Typography.Paragraph>
            </Tooltip>
            <Space
              size={6}
              style={{ fontSize: 11, color: 'var(--ant-color-text-tertiary)' }}
            >
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                #{row.id}
              </span>
              <span>·</span>
              <span>{MODE_LABELS[row.mode]}</span>
            </Space>
          </div>
        ),
      },
      {
        title: '位置',
        width: 110,
        render: (_: unknown, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text style={{ fontSize: 12 }}>
              {row.episode_number != null
                ? `第 ${row.episode_number} 集`
                : `ep#${row.episode_id}`}
            </Typography.Text>
            <Typography.Text
              type="secondary"
              style={{
                fontSize: 12,
                fontFamily: 'var(--ant-font-family-code, monospace)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatVTime(row.v_time)}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: '番剧',
        width: 180,
        ellipsis: true,
        render: (_: unknown, row) =>
          row.anime_title ? (
            <Typography.Text style={{ fontSize: 12 }} ellipsis>
              {row.anime_title}
            </Typography.Text>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Typography.Text>
          ),
      },
      {
        title: '作者',
        width: 200,
        render: (_: unknown, row) => {
          const kind = identityKindOf(row)
          const fp = identityFingerprint(row)
          const copyVal = identityCopyValue(row)
          return (
            <Space direction="vertical" size={2} style={{ rowGap: 0 }}>
              <Space size={4} align="center">
                {kind !== 'none' ? (
                  <Tag
                    bordered={false}
                    color={
                      kind === 'auth'
                        ? 'blue'
                        : kind === 'legacy'
                          ? 'default'
                          : 'gold'
                    }
                    style={{
                      marginRight: 0,
                      fontSize: 11,
                      padding: '0 6px',
                      lineHeight: '16px',
                    }}
                  >
                    {IDENTITY_TAG_LABELS[kind]}
                  </Tag>
                ) : null}
                <Typography.Text style={{ fontSize: 13 }}>
                  {authorDisplay(row)}
                </Typography.Text>
              </Space>
              <Typography.Text
                type="secondary"
                style={{
                  fontSize: 11,
                  fontFamily: 'var(--ant-font-family-code, monospace)',
                }}
                copyable={copyVal ? { text: copyVal } : false}
              >
                {fp}
                {row.user_username ? `  ·  @${row.user_username}` : ''}
              </Typography.Text>
            </Space>
          )
        },
      },
      {
        title: '状态',
        width: 130,
        render: (_: unknown, row) => {
          const bp = statusBadgeProps(row.status)
          return (
            <Space direction="vertical" size={2} style={{ rowGap: 0 }}>
              <Badge {...bp} text={STATUS_LABELS[row.status]} />
              <Typography.Text
                type="secondary"
                style={{ fontSize: 11, paddingLeft: 12 }}
              >
                {row.moderator_kind === 'human'
                  ? '人工'
                  : row.moderator_kind === 'ai'
                    ? 'AI'
                    : '未审'}
                {row.moderated_at
                  ? ` · ${formatCST(row.moderated_at, 'MM-DD')}`
                  : ''}
              </Typography.Text>
            </Space>
          )
        },
      },
      {
        title: '举报',
        width: 70,
        align: 'right',
        render: (_: unknown, row) => {
          if (row.report_count === 0) {
            return (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                —
              </Typography.Text>
            )
          }
          const high = row.report_count >= 3
          return (
            <span
              style={{
                display: 'inline-block',
                padding: high ? '0 8px' : 0,
                background: high ? 'var(--ant-color-error-bg)' : undefined,
                borderRadius: high ? 4 : 0,
                color: high
                  ? 'var(--ant-color-error-text)'
                  : 'var(--ant-color-warning-text)',
                fontWeight: high ? 600 : 500,
                fontVariantNumeric: 'tabular-nums',
                fontSize: 13,
              }}
            >
              {row.report_count}
            </span>
          )
        },
      },
      {
        title: '时间',
        width: 100,
        render: (_: unknown, row) => (
          <Typography.Text
            style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatCST(row.created_at, 'MM-DD HH:mm')}
          </Typography.Text>
        ),
      },
      {
        title: '操作',
        width: 160,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Space size={2}>
            <Space size={0}>
              <Tooltip title={row.status === 'hidden' ? '已隐藏' : '隐藏'}>
                <Button
                  type="text"
                  size="small"
                  icon={<EyeInvisibleOutlined />}
                  disabled={row.status === 'hidden'}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRowSetStatus(row, 'hidden')
                  }}
                />
              </Tooltip>
              <Tooltip title={row.status === 'shadow' ? '已影子' : '影子'}>
                <Button
                  type="text"
                  size="small"
                  icon={<EyeOutlined />}
                  disabled={row.status === 'shadow'}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRowSetStatus(row, 'shadow')
                  }}
                />
              </Tooltip>
              <Tooltip title="删除">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRowDelete(row)
                  }}
                />
              </Tooltip>
            </Space>
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                setDetail(row)
              }}
            >
              详情
            </Button>
          </Space>
        ),
      },
    ],
    [setStatusMutation, deleteMutation],
  )

  const tabEmptyMeta = TAB_META.find((t) => t.key === tab)

  const animeSelectOptions: Array<AnimeOption> = useMemo(() => {
    const list = animeOptionsQuery.data ?? []
    const current = currentAnimeQuery.data
    if (current && !list.find((a) => a.id === current.id)) {
      return [current, ...list]
    }
    return list
  }, [animeOptionsQuery.data, currentAnimeQuery.data])

  /* ── 筛选控件：桌面两行（强收窄 / 切片）原样保留；手机用 AdminFilterBar，
        关键词常驻，其余折叠。折叠面板会卸载子组件，所以搜索框带 defaultValue 回填。 */
  const animeSelect = (
    <Select<number | null>
      allowClear
      showSearch
      placeholder="番剧（输入搜索）"
      style={{ minWidth: 240 }}
      value={animeId ?? undefined}
      onChange={(v) => setAnimeId(v ?? null)}
      onSearch={setAnimeSearchInput}
      filterOption={false}
      loading={animeOptionsQuery.isFetching || currentAnimeQuery.isFetching}
      notFoundContent={
        debouncedAnimeSearch
          ? animeOptionsQuery.isFetching
            ? '搜索中…'
            : '无匹配'
          : '输入关键字搜索'
      }
      options={animeSelectOptions.map((a) => ({ value: a.id, label: a.title }))}
    />
  )
  const episodeSelect = (
    <Select<number | null>
      allowClear
      showSearch
      placeholder={animeId ? '剧集' : '先选番剧'}
      disabled={!animeId}
      style={{ minWidth: 140 }}
      value={episodeId ?? undefined}
      onChange={(v) => setEpisodeId(v ?? null)}
      optionFilterProp="label"
      loading={episodeOptionsQuery.isFetching}
      options={(episodeOptionsQuery.data ?? []).map((e) => ({
        value: e.id,
        label:
          e.episode_number != null ? `第 ${e.episode_number} 集` : `ep#${e.id}`,
      }))}
    />
  )
  const userSearchInput = (
    <DebouncedSearchInput
      key={`user-${searchResetKey}`}
      allowClear
      prefix={<SearchOutlined />}
      placeholder="用户名/昵称"
      defaultValue={debouncedUserSearch}
      onDebouncedChange={setDebouncedUserSearch}
      style={{ width: 180 }}
    />
  )
  const deviceKeyInput = (
    <DebouncedSearchInput
      key={`device-${searchResetKey}`}
      allowClear
      prefix={<SearchOutlined />}
      placeholder="device_key 精确"
      defaultValue={debouncedDeviceKey}
      onDebouncedChange={setDebouncedDeviceKey}
      style={{ width: 200 }}
    />
  )
  const keywordInput = (
    <DebouncedSearchInput
      key={`keyword-${searchResetKey}`}
      allowClear
      prefix={<SearchOutlined />}
      placeholder="关键词（ilike）"
      defaultValue={debouncedKeyword}
      onDebouncedChange={setDebouncedKeyword}
      style={{ width: 200 }}
    />
  )
  const statusSelect = (
    <Select<Status[]>
      mode="multiple"
      allowClear
      placeholder="状态"
      size={isMobile ? undefined : 'small'}
      style={{ minWidth: 160 }}
      value={statusFilter}
      onChange={setStatusFilter}
      options={ALL_STATUS_OPTIONS}
      maxTagCount="responsive"
    />
  )
  const rangePicker = (
    <DatePicker.RangePicker
      size={isMobile ? undefined : 'small'}
      showTime={{ format: 'HH:mm' }}
      format="MM-DD HH:mm"
      value={range}
      onChange={(r) => setRange(r && r[0] && r[1] ? [r[0], r[1]] : null)}
      presets={[
        {
          label: '今天',
          value: [nowCST().startOf('day'), nowCST().endOf('day')],
        },
        {
          label: '近 7 天',
          value: [nowCST().subtract(7, 'day').startOf('day'), nowCST()],
        },
        {
          label: '近 30 天',
          value: [nowCST().subtract(30, 'day').startOf('day'), nowCST()],
        },
      ]}
    />
  )
  const sortSelect = (
    <Select<SortKey>
      size={isMobile ? undefined : 'small'}
      value={sort}
      onChange={setSort}
      style={{ width: 140 }}
      options={Object.entries(SORT_LABELS).map(([value, label]) => ({
        value: value as SortKey,
        label,
      }))}
    />
  )
  // 手机「筛选」角标：除常驻关键词外生效的条件数（排序非默认也算）
  const mobileActiveFilterCount =
    (animeId ? 1 : 0) +
    (episodeId ? 1 : 0) +
    (debouncedUserSearch ? 1 : 0) +
    (debouncedDeviceKey ? 1 : 0) +
    (statusFilter.length > 0 ? 1 : 0) +
    (range ? 1 : 0) +
    (sort !== 'created_desc' ? 1 : 0)

  const bulkDisabled =
    bulkSetStatusMutation.isPending ||
    bulkDeleteMutation.isPending ||
    selectedKeys.length > BULK_HARD_CAP

  const rows = listQuery.data?.rows ?? []
  const toggleSelected = (id: number, checked: boolean) =>
    setSelectedKeys((prev) =>
      checked ? [...prev, id] : prev.filter((k) => k !== id),
    )

  const listEmpty =
    listQuery.isLoading || listQuery.isFetching ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="加载中…" />
    ) : (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Space direction="vertical" size={6} style={{ maxWidth: 360 }}>
            <Typography.Text strong>
              {tabEmptyMeta?.emptyTitle ?? '没有数据'}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {tabEmptyMeta?.emptyHint ?? ''}
            </Typography.Text>
            {hasAnyNarrowing ? (
              <Button
                size="small"
                onClick={clearAllFilters}
                style={{ marginTop: 4 }}
              >
                清空全部筛选
              </Button>
            ) : null}
          </Space>
        }
      />
    )

  return (
    <>
      <Card
        title={
          <Space size={10} align="baseline">
            <span>弹幕审核</span>
            <Typography.Text
              type="secondary"
              className="admin-desktop-only"
              style={{ fontSize: 12, fontWeight: 'normal' }}
            >
              主动巡检 · 被举报的弹幕在 <Link to="/reports">/reports</Link> 处理
            </Typography.Text>
          </Space>
        }
        extra={
          <Tooltip title={isMobile ? '刷新' : undefined}>
            <Button
              icon={<ReloadOutlined />}
              aria-label="刷新"
              onClick={() => {
                listQuery.refetch()
                countsQuery.refetch()
              }}
              loading={listQuery.isFetching && !listQuery.isLoading}
            >
              {isMobile ? null : '刷新'}
            </Button>
          </Tooltip>
        }
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {/* Tab + counts（手机上横向可滚） */}
          <div className={isMobile ? 'admin-scroll-rail' : undefined}>
            <Segmented<TabKey>
              value={tab}
              onChange={(v) => setTab(v as TabKey)}
              options={TAB_META.map(({ key, label }) => {
                const c = countsQuery.data ? countsQuery.data[key] : undefined
                return {
                  value: key,
                  label: (
                    <Space size={6}>
                      <span>{label}</span>
                      {c !== undefined ? (
                        <Badge
                          count={formatBadge(c)}
                          showZero
                          color={
                            c === 0
                              ? 'var(--ant-color-fill-secondary)'
                              : 'var(--ant-color-fill-tertiary)'
                          }
                          style={{
                            color:
                              c === 0
                                ? 'var(--ant-color-text-tertiary)'
                                : 'var(--ant-color-text)',
                            fontWeight: 500,
                          }}
                        />
                      ) : null}
                    </Space>
                  ),
                }
              })}
            />
          </div>

          {isMobile ? (
            <AdminFilterBar
              className="danmaku-filter-bar"
              primary={keywordInput}
              activeCount={mobileActiveFilterCount}
            >
              <div className="admin-filter-grid__full">{animeSelect}</div>
              {episodeSelect}
              {statusSelect}
              {userSearchInput}
              {deviceKeyInput}
              <div className="admin-filter-grid__full">{rangePicker}</div>
              {sortSelect}
              {hasAnyNarrowing ? (
                <Button onClick={clearAllFilters}>清空全部筛选</Button>
              ) : null}
            </AdminFilterBar>
          ) : (
            <>
              {/* 过滤行 1：强收窄（决定结果集大小） */}
              <Space wrap size={8}>
                {animeSelect}
                {episodeSelect}
                {userSearchInput}
                {deviceKeyInput}
                {keywordInput}
              </Space>

              {/* 过滤行 2：弱筛选（在已收窄集合上切片） */}
              <div
                style={{
                  paddingTop: 8,
                  borderTop: '1px dashed var(--ant-color-split)',
                }}
              >
                <Space wrap size={8} align="center">
                  <Typography.Text
                    type="secondary"
                    style={{ fontSize: 12, marginRight: 4 }}
                  >
                    切片：
                  </Typography.Text>
                  {statusSelect}
                  {rangePicker}
                  {sortSelect}
                  {hasAnyNarrowing ? (
                    <Button type="link" size="small" onClick={clearAllFilters}>
                      清空全部筛选
                    </Button>
                  ) : null}
                </Space>
              </div>
            </>
          )}

          {listQuery.error ? (
            <Alert
              type="error"
              showIcon
              closable
              message="加载弹幕失败"
              description={(listQuery.error as Error).message}
              action={
                <Button size="small" onClick={() => listQuery.refetch()}>
                  重试
                </Button>
              }
            />
          ) : null}

          {/* 批量条：不再 sticky，让表格自然滚动；视觉降级到轻边框（手机改为下方吸底条） */}
          {!isMobile && selectedKeys.length > 0 ? (
            <div
              style={{
                background: 'var(--ant-color-fill-quaternary)',
                border: '1px solid var(--ant-color-border)',
                borderRadius: 6,
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              <Typography.Text strong>
                已选 {selectedKeys.length} 条
                {selectedKeys.length > BULK_HARD_CAP ? (
                  <Typography.Text type="danger" style={{ marginLeft: 8 }}>
                    （超过 {BULK_HARD_CAP} 上限）
                  </Typography.Text>
                ) : null}
              </Typography.Text>
              <Space size={4}>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleBulkSetStatus('visible')}
                  disabled={bulkDisabled}
                >
                  恢复可见
                </Button>
                <Button
                  size="small"
                  icon={<EyeInvisibleOutlined />}
                  onClick={() => handleBulkSetStatus('hidden')}
                  disabled={bulkDisabled}
                >
                  批量隐藏
                </Button>
                <Button
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => handleBulkSetStatus('shadow')}
                  disabled={bulkDisabled}
                >
                  批量影子
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={bulkDeleteMutation.isPending}
                  onClick={handleBulkDelete}
                  disabled={
                    bulkSetStatusMutation.isPending ||
                    selectedKeys.length > BULK_HARD_CAP
                  }
                >
                  批量删除
                </Button>
              </Space>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, marginLeft: 'auto' }}
              >
                单次上限 {BULK_HARD_CAP} 条
              </Typography.Text>
              <Button
                type="link"
                size="small"
                onClick={() => setSelectedKeys([])}
                style={{ padding: 0 }}
              >
                清空选择
              </Button>
            </div>
          ) : null}

          {isMobile ? (
            <AdminMobileList
              rows={rows}
              rowKey={(row) => row.id}
              loading={listQuery.isLoading || listQuery.isFetching}
              emptyContent={listEmpty}
              page={page}
              pageSize={PAGE_SIZE}
              total={listQuery.data?.total ?? 0}
              onPageChange={setPage}
              renderItem={(row) => {
                const kind = identityKindOf(row)
                const checked = selectedKeys.includes(row.id)
                return (
                  <article
                    className="admin-mobile-record danmaku-mobile-card"
                    role="button"
                    tabIndex={0}
                    aria-label={`弹幕 #${row.id} 详情`}
                    onClick={() => setDetail(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setDetail(row)
                    }}
                  >
                    <div className="admin-mobile-record__lead">
                      <span
                        className="admin-mobile-record__select"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={checked}
                          aria-label={`选择弹幕 #${row.id}`}
                          onChange={(e) =>
                            toggleSelected(row.id, e.target.checked)
                          }
                        />
                      </span>
                      <div className="danmaku-mobile-card__main">
                        <div className="admin-mobile-record__head">
                          <div className="admin-mobile-record__title danmaku-mobile-card__content">
                            <Typography.Paragraph ellipsis={{ rows: 2 }}>
                              {row.content}
                            </Typography.Paragraph>
                          </div>
                          <Badge
                            {...statusBadgeProps(row.status)}
                            text={STATUS_LABELS[row.status]}
                            className="danmaku-mobile-card__status"
                          />
                        </div>
                        <div className="admin-mobile-record__meta">
                          <span className="danmaku-mobile-card__where">
                            {row.anime_title ?? '—'}
                          </span>
                          <span>
                            {row.episode_number != null
                              ? `第 ${row.episode_number} 集`
                              : `ep#${row.episode_id}`}{' '}
                            · {formatVTime(row.v_time)}
                          </span>
                        </div>
                        <div className="admin-mobile-record__meta">
                          <span>
                            {kind !== 'none' ? (
                              <Tag
                                bordered={false}
                                color={
                                  kind === 'auth'
                                    ? 'blue'
                                    : kind === 'legacy'
                                      ? 'default'
                                      : 'gold'
                                }
                                style={{
                                  marginRight: 4,
                                  fontSize: 11,
                                  padding: '0 6px',
                                  lineHeight: '16px',
                                }}
                              >
                                {IDENTITY_TAG_LABELS[kind]}
                              </Tag>
                            ) : null}
                            {authorDisplay(row)}
                          </span>
                          {row.report_count > 0 ? (
                            <span
                              className={
                                row.report_count >= 3
                                  ? 'danmaku-mobile-card__reports danmaku-mobile-card__reports--high'
                                  : 'danmaku-mobile-card__reports'
                              }
                            >
                              举报 {row.report_count}
                            </span>
                          ) : null}
                          <span>
                            {row.moderator_kind === 'human'
                              ? '人工'
                              : row.moderator_kind === 'ai'
                                ? 'AI'
                                : '未审'}
                          </span>
                          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatCST(row.created_at, 'MM-DD HH:mm')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <AdminRecordActions
                      actions={[
                        {
                          key: 'hidden',
                          label: '隐藏',
                          icon: <EyeInvisibleOutlined />,
                          disabled: row.status === 'hidden',
                          onClick: () => handleRowSetStatus(row, 'hidden'),
                        },
                        {
                          key: 'shadow',
                          label: '影子',
                          icon: <EyeOutlined />,
                          disabled: row.status === 'shadow',
                          onClick: () => handleRowSetStatus(row, 'shadow'),
                        },
                        {
                          key: 'delete',
                          label: '删除',
                          icon: <DeleteOutlined />,
                          danger: true,
                          onClick: () => handleRowDelete(row),
                        },
                      ]}
                    />
                  </article>
                )
              }}
            />
          ) : (
            <div className="admin-table-wrap">
              <Table<DanmakuRow>
                rowKey="id"
                size="middle"
                loading={listQuery.isLoading}
                columns={columns}
                dataSource={rows}
                rowSelection={{
                  selectedRowKeys: selectedKeys,
                  onChange: (keys) => setSelectedKeys(keys as number[]),
                  preserveSelectedRowKeys: true,
                  columnWidth: 44,
                }}
                onRow={(row) => ({
                  onClick: () => setDetail(row),
                  style: { cursor: 'pointer' },
                })}
                scroll={{ x: 1320 }}
                locale={{ emptyText: listEmpty }}
                pagination={{
                  current: page,
                  pageSize: PAGE_SIZE,
                  total: listQuery.data?.total ?? 0,
                  showSizeChanger: false,
                  showTotal: (total) => `共 ${total} 条`,
                  onChange: (p) => setPage(p),
                }}
              />
            </div>
          )}

          {/* 手机批量：吸底条，四个批量动作收进一个下拉 */}
          {isMobile && selectedKeys.length > 0 ? (
            <div className="admin-mobile-bulkbar">
              <span className="admin-mobile-bulkbar__count">
                已选 {selectedKeys.length} 条
                {selectedKeys.length > BULK_HARD_CAP ? (
                  <Typography.Text type="danger">
                    （超过 {BULK_HARD_CAP} 上限）
                  </Typography.Text>
                ) : null}
              </span>
              <Button onClick={() => setSelectedKeys([])}>取消</Button>
              <Dropdown
                trigger={['click']}
                placement="topRight"
                disabled={bulkDisabled}
                menu={{
                  items: [
                    {
                      key: 'visible',
                      label: '恢复可见',
                      icon: <EyeOutlined />,
                    },
                    {
                      key: 'hidden',
                      label: '批量隐藏',
                      icon: <EyeInvisibleOutlined />,
                    },
                    {
                      key: 'shadow',
                      label: '批量影子',
                      icon: <EyeOutlined />,
                    },
                    { type: 'divider' },
                    {
                      key: 'delete',
                      label: '批量删除',
                      icon: <DeleteOutlined />,
                      danger: true,
                    },
                  ],
                  onClick: ({ key }) => {
                    if (key === 'delete') handleBulkDelete()
                    else handleBulkSetStatus(key as SetableStatus)
                  },
                }}
              >
                <Button
                  type="primary"
                  loading={
                    bulkSetStatusMutation.isPending ||
                    bulkDeleteMutation.isPending
                  }
                >
                  批量操作 <DownOutlined />
                </Button>
              </Dropdown>
            </div>
          ) : null}
        </Space>
      </Card>

      <DetailDrawer
        row={detail}
        onClose={() => setDetail(null)}
        onSetStatus={(status, note) => {
          if (!detail) return
          setStatusMutation.mutate(
            { id: detail.id, status, note: note?.trim() || null },
            { onSuccess: () => setDetail(null) },
          )
        }}
        onDelete={(note) => {
          if (!detail) return
          deleteMutation.mutate({ id: detail.id, note })
        }}
        onBanIdentity={(note) => {
          if (!detail) return
          const identity = authorRPCIdentity(detail)
          if (!identity) {
            message.error(
              '该弹幕无可识别身份（user_id / legacy_user_id / device_key 都为空）',
            )
            return
          }
          banUserMutation.mutate({ identity, note })
        }}
        statusPending={setStatusMutation.isPending}
        deletePending={deleteMutation.isPending}
        banPending={banUserMutation.isPending}
      />
    </>
  )
}

/* ── 详情抽屉 ────────────────────────────────────────────────── */

function DetailDrawer({
  row,
  onClose,
  onSetStatus,
  onDelete,
  onBanIdentity,
  statusPending,
  deletePending,
  banPending,
}: {
  row: DanmakuRow | null
  onClose: () => void
  onSetStatus: (status: SetableStatus, note: string) => void
  onDelete: (note: string) => void
  onBanIdentity: (note: string) => void
  statusPending: boolean
  deletePending: boolean
  banPending: boolean
}) {
  const { message, modal } = AntdApp.useApp()
  const isMobile = useIsMobile()
  const [note, setNote] = useState('')
  const [noteVisible, setNoteVisible] = useState(false)

  useEffect(() => {
    setNote('')
    setNoteVisible(false)
  }, [row?.id])

  const confirmDelete = () => {
    if (note.trim().length < 5) {
      setNoteVisible(true)
      message.warning('删除必须填写不少于 5 字的备注')
      return
    }
    modal.confirm({
      title: '硬删该弹幕？',
      content:
        '不可恢复。触发器会级联清掉所有关联举报。审计日志会保留 200 字内容摘要。',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => onDelete(note),
    })
  }

  const confirmBan = () => {
    if (!row) return
    const identity = authorRPCIdentity(row)
    if (!identity) {
      message.error('该弹幕无可识别身份')
      return
    }
    if (note.trim().length < 5) {
      setNoteVisible(true)
      message.warning('Ban 必须填写不少于 5 字的备注')
      return
    }
    modal.confirm({
      title: '一键 shadow 该身份所有可见弹幕？',
      content: (
        <Space direction="vertical" size={6} style={{ marginTop: 8 }}>
          <Typography.Text>
            身份：
            <Typography.Text code>
              {identity.kind} = {identity.value}
            </Typography.Text>
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            会把该身份所有 status=visible 的历史弹幕改为
            shadow（仅作者本人可见）。 硬上限 5000 条；超过会失败。每条独立
            audit log。
          </Typography.Text>
        </Space>
      ),
      okText: '执行',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => onBanIdentity(note),
    })
  }

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width={
        isMobile
          ? '100%'
          : Math.min(
              720,
              Math.floor(
                typeof window !== 'undefined' ? window.innerWidth * 0.85 : 720,
              ),
            )
      }
      title={
        row ? (
          <Space size={8} align="center">
            <Badge
              {...statusBadgeProps(row.status)}
              text={STATUS_LABELS[row.status]}
            />
            <span>弹幕 #{row.id}</span>
          </Space>
        ) : (
          ''
        )
      }
      destroyOnHidden
    >
      {!row ? null : (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {/* 1) 内容 */}
          <div>
            <Typography.Paragraph
              style={{
                whiteSpace: 'pre-wrap',
                padding: 16,
                background: 'var(--ant-color-fill-tertiary)',
                borderRadius: 8,
                marginBottom: 8,
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {row.content}
            </Typography.Paragraph>
            <Space
              size={10}
              wrap
              style={{ fontSize: 12, color: 'var(--ant-color-text-tertiary)' }}
            >
              {row.anime_title ? (
                <span>
                  {row.anime_title}
                  {row.episode_number != null
                    ? ` · 第 ${row.episode_number} 集`
                    : ''}
                </span>
              ) : (
                <span>ep#{row.episode_id}</span>
              )}
              <span>·</span>
              <span
                style={{
                  fontFamily: 'var(--ant-font-family-code, monospace)',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatVTimeFull(row.v_time)}
              </span>
              <span>·</span>
              <span>{MODE_LABELS[row.mode]}</span>
              <span>·</span>
              <span>{authorDisplay(row)}</span>
              {row.user_username ? <span>@{row.user_username}</span> : null}
              <span>·</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatCST(row.created_at, 'YYYY-MM-DD HH:mm')}
              </span>
            </Space>
          </div>

          {/* 2) 常规操作 */}
          <div>
            <Typography.Title level={5} style={{ marginBottom: 8 }}>
              常规操作
            </Typography.Title>
            <Space wrap>
              <Radio.Group
                value={row.status}
                onChange={(e) => {
                  const next = e.target.value as SetableStatus
                  if (next === row.status) return
                  onSetStatus(next, note)
                }}
                disabled={
                  statusPending ||
                  (row.status !== 'visible' &&
                    row.status !== 'hidden' &&
                    row.status !== 'shadow')
                }
                optionType="button"
                buttonStyle="solid"
                options={SETABLE_STATUS_OPTIONS}
              />
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={deletePending}
                onClick={confirmDelete}
              >
                删除该条
              </Button>
            </Space>
            {noteVisible ? (
              <Input.TextArea
                autoFocus
                placeholder="备注（删除 / Ban 必填 ≥5 字；其他可选，写入审计日志）"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={500}
                showCount
                style={{ marginTop: 12 }}
              />
            ) : (
              <Button
                type="link"
                size="small"
                onClick={() => setNoteVisible(true)}
                style={{ paddingLeft: 0, marginTop: 8 }}
              >
                + 添加备注（可选）
              </Button>
            )}
          </div>

          {/* 3) 高风险动作区 —— 与常规操作做空间隔离，提升等级 */}
          <div
            style={{
              border: '1px dashed var(--ant-color-error-border)',
              borderRadius: 6,
              padding: 12,
              background: 'var(--ant-color-error-bg)',
            }}
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              <Space align="center">
                <Typography.Title
                  level={5}
                  style={{ margin: 0, color: 'var(--ant-color-error-text)' }}
                >
                  高风险动作
                </Typography.Title>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  影响该身份的全部历史弹幕（最多 5000 条）
                </Typography.Text>
              </Space>
              <Tooltip
                title={
                  authorRPCIdentity(row)
                    ? '一键把该身份所有 visible 弹幕改为 shadow'
                    : '该弹幕无可识别身份（三个身份字段都为空）'
                }
              >
                <Button
                  danger
                  icon={<StopOutlined />}
                  loading={banPending}
                  disabled={!authorRPCIdentity(row)}
                  onClick={confirmBan}
                >
                  Ban 该身份所有弹幕
                </Button>
              </Tooltip>
            </Space>
          </div>

          {/* 4) 高举报警示 */}
          {row.report_count > 0 ? (
            <Alert
              type={row.report_count >= 3 ? 'error' : 'warning'}
              showIcon
              message={`该弹幕收到 ${row.report_count} 次举报`}
              description={
                <>
                  按举报 reason 处理可去 <Link to="/reports">/reports</Link>{' '}
                  找此 target。
                </>
              }
            />
          ) : null}

          {/* 5) 折叠次要信息 */}
          <Collapse
            ghost
            items={[
              {
                key: 'meta',
                label: <Typography.Text strong>基础信息</Typography.Text>,
                children: (
                  <Descriptions
                    column={isMobile ? 1 : 2}
                    layout={isMobile ? 'vertical' : 'horizontal'}
                    size="small"
                    bordered
                    items={[
                      {
                        label: '剧集',
                        children: row.anime_title ? (
                          <span>
                            {row.anime_title}
                            {row.episode_number != null
                              ? `（第 ${row.episode_number} 集）`
                              : ''}
                          </span>
                        ) : (
                          `ep#${row.episode_id}`
                        ),
                      },
                      {
                        label: '播放点 / 模式',
                        children: (
                          <span
                            style={{
                              fontFamily:
                                'var(--ant-font-family-code, monospace)',
                            }}
                          >
                            {formatVTimeFull(row.v_time)} ·{' '}
                            {MODE_LABELS[row.mode]}
                          </span>
                        ),
                      },
                      {
                        label: '颜色',
                        children: (
                          <Space size={6}>
                            <span
                              style={{
                                display: 'inline-block',
                                width: 14,
                                height: 14,
                                background: row.color,
                                borderRadius: 3,
                                border: '1px solid var(--ant-color-border)',
                              }}
                            />
                            <Typography.Text
                              style={{
                                fontSize: 11,
                                fontFamily:
                                  'var(--ant-font-family-code, monospace)',
                              }}
                            >
                              {row.color}
                            </Typography.Text>
                          </Space>
                        ),
                      },
                      {
                        label: '举报数',
                        children: (
                          <span
                            style={{
                              color:
                                row.report_count > 0
                                  ? 'var(--ant-color-error-text)'
                                  : undefined,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            {row.report_count}
                          </span>
                        ),
                      },
                      {
                        label: '作者',
                        span: 2,
                        children: (
                          <Space direction="vertical" size={0}>
                            <Space size={6}>
                              <Avatar size={20}>
                                {authorDisplay(row).slice(0, 1)}
                              </Avatar>
                              <span>{authorDisplay(row)}</span>
                              {identityKindOf(row) !== 'none' ? (
                                <Tag
                                  bordered={false}
                                  color={
                                    identityKindOf(row) === 'auth'
                                      ? 'blue'
                                      : identityKindOf(row) === 'legacy'
                                        ? 'default'
                                        : 'gold'
                                  }
                                  style={{ marginRight: 0, fontSize: 11 }}
                                >
                                  {
                                    IDENTITY_TAG_LABELS[
                                      identityKindOf(row) as
                                        | 'auth'
                                        | 'legacy'
                                        | 'dev'
                                    ]
                                  }
                                </Tag>
                              ) : null}
                            </Space>
                            {row.user_username ? (
                              <Typography.Text
                                type="secondary"
                                style={{ fontSize: 11 }}
                              >
                                @{row.user_username}
                              </Typography.Text>
                            ) : null}
                            {row.user_id ? (
                              <Typography.Text
                                type="secondary"
                                copyable={{ text: row.user_id }}
                                style={{
                                  fontSize: 11,
                                  fontFamily:
                                    'var(--ant-font-family-code, monospace)',
                                }}
                              >
                                user_id {row.user_id}
                              </Typography.Text>
                            ) : row.legacy_user_id ? (
                              <Typography.Text
                                type="secondary"
                                style={{ fontSize: 11 }}
                              >
                                legacy_user_id #{row.legacy_user_id}（未认领）
                              </Typography.Text>
                            ) : null}
                            {row.device_key ? (
                              <Typography.Text
                                type="secondary"
                                copyable={{ text: row.device_key }}
                                style={{
                                  fontSize: 11,
                                  fontFamily:
                                    'var(--ant-font-family-code, monospace)',
                                }}
                              >
                                device_key {row.device_key}
                              </Typography.Text>
                            ) : null}
                          </Space>
                        ),
                      },
                      {
                        label: 'IP',
                        span: 2,
                        children: <IpAddressLookup ip={row.ip_address} />,
                      },
                    ]}
                  />
                ),
              },
              {
                key: 'history',
                label: (
                  <Typography.Text strong>
                    审核历史
                    {row.moderator_kind ? (
                      <Typography.Text
                        type="secondary"
                        style={{
                          marginLeft: 8,
                          fontWeight: 'normal',
                          fontSize: 12,
                        }}
                      >
                        · {row.moderator_kind === 'human' ? '人工' : 'AI'} ·{' '}
                        {formatDateTime(row.moderated_at)}
                      </Typography.Text>
                    ) : (
                      <Typography.Text
                        type="secondary"
                        style={{
                          marginLeft: 8,
                          fontWeight: 'normal',
                          fontSize: 12,
                        }}
                      >
                        · 尚未审核
                      </Typography.Text>
                    )}
                  </Typography.Text>
                ),
                children: row.moderator_kind ? (
                  <Descriptions
                    column={1}
                    layout={isMobile ? 'vertical' : 'horizontal'}
                    size="small"
                    bordered
                    items={[
                      {
                        label: '审核者',
                        children:
                          row.moderator_kind === 'human' ? '人工' : 'AI',
                      },
                      {
                        label: '审核时间',
                        children: formatDateTime(
                          row.moderated_at,
                          'YYYY-MM-DD HH:mm:ss',
                        ),
                      },
                      {
                        label: '操作者 UID',
                        children: row.moderated_by ?? '—',
                      },
                    ]}
                  />
                ) : (
                  <Typography.Text type="secondary">尚未审核过</Typography.Text>
                ),
              },
            ]}
          />
        </Space>
      )}
    </Drawer>
  )
}
